import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { GraduationCap, ArrowRight, Phone, Mail, MapPin, CheckCircle2, Clock, Users, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { MarketingNav } from '../components/MarketingLayout';
import { Card, CardContent } from '../components/ui/card';

const CALENDLY_URL = "https://calendly.com/taimur-ivyleaguehelp/30min";

export default function ConsultationPage() {
  useEffect(() => {
    // Load Calendly widget script
    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    document.head.appendChild(script);
    return () => { document.head.removeChild(script); };
  }, []);

  return (
    <div className="min-h-screen bg-white pt-16" data-testid="consultation-page">
      {/* Nav */}
      <MarketingNav />

      {/* Hero */}
      <section className="bg-[#0F392B] text-white py-14">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <p className="text-[#D4AF37] text-xs font-semibold uppercase tracking-wider mb-3">Free 30-Minute Strategy Session</p>
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight leading-tight mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Schedule Your Free Consultation
          </h1>
          <p className="text-base text-white/70 max-w-xl mx-auto leading-relaxed">
            Meet with an Ivy League Consulting advisor to discuss your admissions goals, review your profile, and get an honest assessment of your strategy — completely free.
          </p>
        </div>
      </section>

      {/* What to Expect */}
      <section className="max-w-4xl mx-auto px-4 py-10">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-10">
          {[
            { icon: Clock, label: '30 Minutes', desc: 'Focused and actionable' },
            { icon: Users, label: 'One-on-One', desc: 'With a senior advisor' },
            { icon: Shield, label: 'No Commitment', desc: 'Zero pressure to sign up' },
            { icon: CheckCircle2, label: 'Free Assessment', desc: 'Honest profile review' },
          ].map((item, i) => (
            <Card key={i} className="border-[#E2E2E2]">
              <CardContent className="p-4 text-center">
                <item.icon className="h-5 w-5 text-[#0F392B] mx-auto mb-2" strokeWidth={1.5} />
                <p className="text-xs font-semibold">{item.label}</p>
                <p className="text-[10px] text-muted-foreground mt-0.5">{item.desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Calendly Embed */}
      <section className="max-w-4xl mx-auto px-4 pb-12" data-testid="calendly-embed-section">
        <Card className="border-[#E2E2E2] overflow-hidden">
          <CardContent className="p-0">
            <div className="px-6 py-4 border-b border-[#E2E2E2] bg-[#F9F9F7]">
              <h2 className="text-sm font-semibold" style={{ fontFamily: "'Playfair Display', serif" }}>Pick a Time That Works for You</h2>
              <p className="text-[11px] text-muted-foreground mt-0.5">All times shown in your local timezone</p>
            </div>
            <div
              className="calendly-inline-widget"
              data-url={`${CALENDLY_URL}?hide_gdpr_banner=1&background_color=ffffff&text_color=1a1a1a&primary_color=0F392B`}
              style={{ minWidth: '320px', height: '700px' }}
              data-testid="calendly-widget"
            />
          </CardContent>
        </Card>
      </section>

      {/* What We'll Cover */}
      <section className="bg-[#F9F9F7] py-12">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-lg font-semibold text-center mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
            What We'll Cover in Your Session
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              "Review your academic profile (GPA, test scores, coursework)",
              "Assess extracurricular strength and positioning",
              "Discuss your target school list and strategy",
              "Identify gaps and areas for improvement",
              "Outline a personalized application timeline",
              "Answer your questions about the admissions process",
              "Explain how our platform and services can help",
              "Provide an honest assessment — even if we're not the right fit",
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-2.5 p-3 bg-white rounded-lg border border-[#E2E2E2]">
                <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                <span className="text-xs text-muted-foreground">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Info */}
      <section className="max-w-3xl mx-auto px-4 py-12">
        <Card className="border-[#E2E2E2]">
          <CardContent className="p-6">
            <h3 className="text-sm font-semibold mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>Prefer to Reach Out Directly?</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <a href="tel:4076837689" className="flex items-center gap-2.5 text-sm text-muted-foreground hover:text-foreground">
                <Phone className="h-4 w-4 text-[#0F392B]" /> (407) 683-7689
              </a>
              <a href="mailto:taimur@ivyleaguehelp.com" className="flex items-center gap-2.5 text-sm text-muted-foreground hover:text-foreground">
                <Mail className="h-4 w-4 text-[#0F392B]" /> taimur@ivyleaguehelp.com
              </a>
              <div className="flex items-center gap-2.5 text-sm text-muted-foreground">
                <MapPin className="h-4 w-4 text-[#0F392B]" /> Orlando, FL
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#E2E2E2] bg-white py-8">
        <div className="max-w-6xl mx-auto px-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <GraduationCap className="h-4 w-4 text-[#0F392B]" strokeWidth={1.5} />
            <span className="text-xs text-muted-foreground">Ivy League Consulting</span>
          </div>
          <p className="text-[10px] text-muted-foreground">&copy; {new Date().getFullYear()} Ivy League Consulting. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
